import { formatDateTimeNowForFirestore } from "util/function/function";
import { deleteImages, uploadCommunityImages } from "firebase_/fire_storage";
import { newCommunitySend } from "api/post/post_community";
import {
  getCommunityListApi,
  getCommunityAllApi,
  getCommunityDetailApi,
  getCommunityCommentsApi,
  getCommunityCountApi,
  createCommunityPostApi,
  updateCommunityPostApi,
  deleteCommunityPostApi,
  toggleLikeApi,
  addCommentApi,
  updateCommentApi,
  deleteCommentApi,
  createReportApi,
  checkReportBeforeApi,
  blockUserApi,
} from "api/community";

// Firestore → REST 이관. 기존 호출측 화면이 기대하는 return shape을 최대한 보존한다.
// 커서 기반 페이지네이션(lastCreateDate)은 서버가 오프셋 기반으로 전환되면서 page/limit을 사용한다.
// 호출측은 amount와 lastCreateDate를 page 계산에 활용한다.

/// 이야기 전체 개수 가져오기
export const getCommunityCount = async (type, _blockData) => {
  try {
    return await getCommunityCountApi(type);
  } catch (error) {
    console.error("failed getCommunityCount:", error);
    return 0;
  }
};

// blockData 기반 필터링은 서버에서 미지원 — 클라이언트 측에서 제외.
function excludeBlocked(list, blockData) {
  if (!blockData || blockData.length === 0) return list;
  const blockedIds = new Set(blockData.map((b) => b.id));
  return list.filter((row) => !blockedIds.has(row.id));
}

/// ✅ 이야기 데이터 가져오기 (페이지네이션 — 최신순)
export const getCommunityData = async (
  type,
  amount,
  lastCreateDate,
  blockData,
) => {
  try {
    // lastCreateDate 는 이전 페이지 마지막 row의 createDate. 서버는 page 기반이므로
    // 호출측 페이지 관리 로직을 최대한 건드리지 않기 위해 실제 오프셋 계산은 호출측이 처리하지 않고
    // 여기서는 호출측이 넘겨준 type 으로 첫 페이지(또는 페이지 1)를 반환한다.
    // 보다 정교한 페이지네이션은 별도 훅에서 처리한다.
    const pageSize = amount || 15;
    // lastCreateDate 가 있으면 다음 페이지로 취급(단순화).
    const page = lastCreateDate ? 2 : 1;
    const list = await getCommunityListApi(type, page, pageSize);
    return excludeBlocked(list, blockData);
  } catch (error) {
    console.error("failed getCommunityData:", error);
    return [];
  }
};

export const getCommunityDataPrev = async (
  type,
  amount,
  lastCreateDate,
  blockData,
) => {
  try {
    const pageSize = amount || 15;
    const list = await getCommunityListApi(type, 1, pageSize);
    return excludeBlocked(list, blockData);
  } catch (error) {
    console.error("failed getCommunityDataPrev:", error);
    return [];
  }
};

export const getCommunityAllData = async () => {
  try {
    return await getCommunityAllApi();
  } catch (error) {
    console.error("failed getCommunityAllData:", error);
    return [];
  }
};

/// ✅ 게시글 정보 + 댓글
export const getBoardDataWithComment = async (boardDocId) => {
  try {
    const [board, comment] = await Promise.all([
      getCommunityDetailApi(boardDocId),
      getCommunityCommentsApi(boardDocId),
    ]);
    return {
      board: board[0],
      comment: Array.isArray(comment)
        ? comment.filter((c) => c.status === "0")
        : [],
    };
  } catch (error) {
    console.error("failed getBoardDataWithComment:", error);
    return {};
  }
};

/// ✅ 게시글 정보만
export const getBoardData = async (boardDocId) => {
  try {
    const board = await getCommunityDetailApi(boardDocId);
    return board[0];
  } catch (error) {
    console.error("failed getBoardData:", error);
    return [];
  }
};

/// ✅ 좋아요 수 업데이트
export const updateLikeByCommunity = async (boardDocId, userId) => {
  try {
    const board = await getBoardData(boardDocId);
    if (!board) return;
    const alreadyLiked = Array.isArray(board.liked) && board.liked.includes(userId);
    await toggleLikeApi({
      docId: boardDocId,
      userId,
      action: alreadyLiked ? "unlike" : "like",
    });
    return alreadyLiked ? "delete" : "add";
  } catch (error) {
    console.error("failed updateLikeByCommunity:", error);
    return;
  }
};

/// ✅ 댓글 등록
export const addComment = async (userId, userName, text, boardDocId) => {
  try {
    const result = await addCommentApi({
      communityDocId: boardDocId,
      userId,
      userName,
      text,
    });
    if (result && result.success) return "success";
    return;
  } catch (error) {
    console.error("failed addComment:", error);
    return;
  }
};

/// ✅ 댓글 수정
export const editComment = async (boardDocId, editValue, commentDocId) => {
  try {
    const result = await updateCommentApi({
      communityDocId: boardDocId,
      commentDocId,
      text: editValue,
    });
    if (result && result.success) return "success";
    return;
  } catch (error) {
    console.error("failed editComment:", error);
    return;
  }
};

/// ✅ 댓글 삭제
export const deleteComment = async (boardDocId, commentDocId) => {
  try {
    const result = await deleteCommentApi({
      communityDocId: boardDocId,
      commentDocId,
    });
    if (result && result.success) return "success";
    return;
  } catch (error) {
    console.error("failed deleteComment:", error);
    return;
  }
};

/// ✅ 댓글/글 신고
export const reportComment = async (targetId, targetName, reason, userId) => {
  try {
    const result = await createReportApi({
      id: targetId,
      name: targetName,
      reason,
      reportedBy: userId,
    });
    if (result && result.success) return "success";
    return;
  } catch (error) {
    console.error("failed reportComment:", error);
    return;
  }
};

/// ✅ 글 삭제
export const deletePost = async (docId) => {
  try {
    const result = await deleteCommunityPostApi(docId);
    if (result && result.success) return "success";
    return;
  } catch (error) {
    console.error("failed deletePost:", error);
    return;
  }
};

/// ✅ 이미 신고했던 댓글인지
export const checkReportedBefore = async (targetId, userId) => {
  try {
    const list = await checkReportBeforeApi(targetId, userId);
    if (list && list.length > 0) return "already";
    return;
  } catch (error) {
    console.error("failed checkReportedBefore:", error);
    return;
  }
};

/// ✅ 댓글 차단 (유저 블록)
export const blockComment = async (
  targetUserId,
  targetUserName,
  userId,
  userDocId,
) => {
  try {
    const result = await blockUserApi({
      userDocId,
      blockerId: userId,
      blockedUserId: targetUserId,
      blockedUserName: targetUserName,
    });
    if (result && result.success) return "success";
    if (result && result.error === "already_blocked") return "already";
    return;
  } catch (error) {
    console.error("failed blockComment:", error);
    return;
  }
};

/// ✅ 커뮤니티 글 등록
export async function createCommunityPost(
  title,
  body,
  type,
  userData,
  selectedImages = [],
) {
  try {
    // 1. 글 먼저 생성 (docId 발급)
    const created = await createCommunityPostApi({
      title,
      body,
      type,
      id: userData.id,
      name: userData.name,
      imageUrls: [],
    });
    if (!created || !created.success) return false;
    const docId = created.docId;

    // 2. 이미지 업로드 (있을 때) 후 업데이트
    let imageUrls = [];
    if (selectedImages && selectedImages.length > 0) {
      imageUrls = await uploadCommunityImages(docId, selectedImages);
      await updateCommunityPostApi({ docId, title, body, imageUrls });
    }

    // 3. 푸시 알림 — 커뮤니티 새 글 알림은 더 이상 발송하지 않음 (요청 사항)
    // (대신 내 글에 댓글이 달릴 때만 작성자에게 알림이 가도록 백엔드 addComment 가 처리한다)

    return true;
  } catch (error) {
    console.error("커뮤니티 글 등록 실패:", error);
    return false;
  }
}

/// ✅ 커뮤니티 글 수정
export async function editCommunityPost(
  docId,
  title,
  body,
  selectedImages = [],
) {
  try {
    // 기존 글의 기존 이미지 URL 가져오기
    const prev = (await getCommunityDetailApi(docId))[0];
    const prevImageUrls = (prev && prev.imageUrls) || [];

    // selectedImages 는 File 또는 기존 url 혼합.
    const keepUrls = selectedImages.filter((img) => typeof img === "string");
    const newFiles = selectedImages.filter((img) => typeof img !== "string");

    // 새 파일 업로드
    let uploadedUrls = [];
    if (newFiles.length > 0) {
      uploadedUrls = await uploadCommunityImages(docId, newFiles);
    }

    // 삭제된 url 제거
    const deletedUrls = prevImageUrls.filter(
      (prevUrl) => !keepUrls.includes(prevUrl),
    );
    if (deletedUrls.length > 0) {
      await deleteImages(deletedUrls);
    }

    const finalImageUrls = [...keepUrls, ...uploadedUrls];

    const result = await updateCommunityPostApi({
      docId,
      title,
      body,
      imageUrls: finalImageUrls,
    });

    return !!(result && result.success);
  } catch (error) {
    console.error("커뮤니티 글 수정 실패:", error);
    return false;
  }
}
